import React, { Fragment, useEffect, useState } from "react";
import { number, object, shape, string } from "prop-types";
import get from "lodash/get";
import throttle from "lodash/throttle";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "@quintype/components";
import {
  AuthorWithTime,
  CollectionName,
  FourColPortraitStories,
  StoryCard,
  Subheadline,
  ThreeColGrid,
} from "../../arrow";
import { HeroImage } from "../../arrow/components/Atoms/HeroImage";
import FourColGrid from "../../arrow/components/Rows/FourColGrid";
import { STATIC_KEYWORDS } from "../../constants";
import { Button, Headline } from "../atoms";
import { SvgIconHandler } from "../atoms/SvgIconHandler";
import { useDeviceType } from "../customHooks/useDeviceType";
import { STORY_HEADER_IN_VIEW } from "../store/actions";
import ListingAdSlot from "../molecules/ListingAdSlot";
import { getLoadMoreStories } from "../utils";
import { getCategoryAndSubCategory, getTemplateName } from "../../ga-utils";
import { ArrowFourColPortraitStories } from "../collection-templates/arrow-rows/four-col-portrait-stories";
import { ArrowThreeColGrid } from "../collection-templates/arrow-rows/three-col-grid";
import YouTube from "react-youtube";
import { BrowserView, MobileView } from "react-device-detect";
import "./section.m.css";
import LiveButton from "../atoms/live-button";

const YoutubeIframe = ({ collection }) => {
  const onPlayerReady = (event) => {
    event.target.playVideo();
  };

  return (
    <div styleName="yt-container">
      <YouTube
        id="videoFrame"
        videoId={collection.summary}
        className="yt-iframe"
        opts={{
          width: "100%",
          height: "100%",
          playerVars: {
            autoplay: 1,
            mute: 1,
            controls: 1,
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
          },
        }}
        onReady={onPlayerReady}
      />
    </div>
  );
};

const SectionPage = (props) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const dispatch = useDispatch();
  const qtConfig = useSelector((state) => get(state, ["qt", "config"], {}));
  const noStoriesFound =
    props.pageType === "collection-page" ? "No Collection Stories Found" : "No Section Stories Found";
  const isMagazineLandingPage = get(props, ["isMagazinePage"], false);
  const magazineTitle = get(props, ["magazineIssueTitle"], "");
  const childCollections = (get(props, ["data", "collection", "items"]) || []).filter(
    (item) =>
      item.type === "collection" &&
      ((item.items?.length ?? 0) > 0 || item?.["associated-metadata"]?.["show_collection_name"] === true)
  );
  const isVisualStory = props?.data?.section?.slug === "visual-stories";
  const isAdfreePage = get(qtConfig, ["subscriber"]) === "true";

  const sectionCollection = get(props, ["data", "collection"], {});

  const isBudgetSponsor = get(sectionCollection, ["metadata", "sponsored-logo"], []);
  const isDigitalCreator = get(sectionCollection, ["metadata", "creator-of-the-month", "0", "name"], "") === 'true' ? true : false;

  let sponsorList = [];
  try {
    sponsorList =
      typeof isBudgetSponsor === "string"
        ? JSON.parse(isBudgetSponsor)
        : isBudgetSponsor;
  } catch (e) {
    console.error("Sponsor logo JSON parse failed", e, isBudgetSponsor);
  }
  const [activeSponsorContainer, setActiveSponsorContainer] = useState(0);
  useEffect(() => {
    if (sponsorList.length <= 1) return;
    const interval = setInterval(() => {
      setActiveSponsorContainer((prev) => (prev + 1) % sponsorList.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [sponsorList.length]);

  const isBudgetCollection = get(sectionCollection, ["metadata", "isbudget"], []);
  let isBudget = false;
  if (isBudgetCollection.length > 0) {
    isBudgetCollection.forEach((item) => {
      if (item.name === "true") {
        isBudget = true;
      }
    });
  }
  const isYoutubeTitle = get(sectionCollection, ["metadata", "youtube-title"], "");
  const hasSummaryAndYoutubeTitle = !!sectionCollection?.summary?.trim() && !!isYoutubeTitle?.trim();
  const parentSecStories = get(sectionCollection, ["items"], []).filter((item) => item?.type === "story");
  const maxStoriesLimit = props?.showAllStories
    ? parentSecStories.length
    : !isBudget && hasSummaryAndYoutubeTitle
      ? 8
      : 9;
  const parentSectionFirstStory = get(parentSecStories, ["0", "story"], {});
  const isLiveBlog = get(parentSectionFirstStory, ["story-template"], "") === "live-blog";
  const liveBlogStoryId = get(parentSectionFirstStory, ["id"], null);
  const parentSecAltHeadline = get(parentSectionFirstStory, ["alternative", "home", "default", "headline"], "");
  const { headline, authors, "published-at": publishedAt, slug } = parentSectionFirstStory;
  let storyStartIndex = 1;
  if (!isBudget && hasSummaryAndYoutubeTitle) {
    storyStartIndex = 0;
  }

  const secToNineParentStories =
    (isVisualStory ? parentSecStories?.slice(0, 4) : parentSecStories?.slice(storyStartIndex, maxStoriesLimit)) || [];
  const nineTo15ParentStories = isVisualStory
    ? parentSecStories?.slice(4, 12)
    : parentSecStories?.slice(
      !isBudget && hasSummaryAndYoutubeTitle ? maxStoriesLimit + 1 : maxStoriesLimit,
      isAdfreePage ? 15 : 14
    ) || [];

  const [storiesToRender, setStoriesToRender] = useState(isVisualStory ? 12 : 15);
  const [apiTitles, setApiTitles] = useState([]);
  const [apiTimes, setApiTimes] = useState([]);
  const [otherParentSecStories, setOtherParentSecStories] = useState(nineTo15ParentStories);
  const [haveMoreStories, setHaveMoreStories] = useState(true);
  const storiesLoadLimit = isVisualStory ? 8 : 5;

  const { slug: sectionSlug, id: parentSectionId } = get(props, ["data", "section"], {});
  const isVideoSection = sectionSlug?.includes("videos");
  const isPhotoSection = sectionSlug?.includes("photos");
  const isVideosPhotosSection = isVideoSection || isPhotoSection;
  const newTitle = { videos: "All Videos News", photos: "All Photos News" };
  const parentSectionCollectionName = get(props, ["data", "collection", "name"], "");
  //const parentSectionCollectionSummary = get(props, ["data", "collection", "summary"], "");
  //const qtState = useSelector((state) => get(state, ["qt"], {}));
  const budgetBannerDesktopImageUrl = get(sectionCollection, ["metadata", "desktop-image"], false);
  const budgetBannerMobileImageUrl = get(sectionCollection, ["metadata", "mobile-image"], false);
  const isBlackBGCollection = get(sectionCollection, ["metadata", "black-background"], []);
  let isBlackBg = false;
  if (isBlackBGCollection.length > 0) {
    isBlackBGCollection.forEach((item) => {
      if (item.name === "true") {
        isBlackBg = true;
      }
    });
  }

  const newSectionTitle =
    newTitle[parentSectionCollectionName.toLowerCase()] || get(props, ["data", "collection", "name"], "");

  const allSections = get(props, ["config", "sections"], []).filter(
    (section) => section?.["parent-id"] === parentSectionId
  );

  const childParentSectionId = get(props, ["data", "section", "parent-id"], "");
  const childParentSection = get(props, ["config", "sections"], []).find(
    (section) => section?.id === childParentSectionId
  );

  const borderClass = isVideosPhotosSection ? "white-bottom-border" : "";
  const subHeadlineClass = isVideosPhotosSection ? "white-sub-headline" : "";

  // hard coded for now, will made dynamic in future sprints
  const premiumUser = false;
  const showDarkMode = sectionSlug?.toLowerCase() === "photos" || sectionSlug?.toLowerCase() === "videos";
  if (childParentSection) {
    const parentChildSections = get(props, ["config", "sections"], []).filter(
      (section) => section?.["parent-id"] === childParentSectionId
    );
    allSections.push(childParentSection, ...parentChildSections);
  } else {
    allSections.unshift(get(props, ["data", "section"], {}));
  }

  const { ALL, NEWS, LOAD_MORE } = STATIC_KEYWORDS;
  const deviceType = useDeviceType();
  const desktop = deviceType === "desktop";
  const currentPath = get(props, ["currentPath"], "");

  const qtStates = useSelector((state) => state);
  const storyHeaderInView = get(qtStates, "storyHeaderInView", true);

  const handleScroll = () => {
    if (window.scrollY > 200 && storyHeaderInView && desktop) {
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: false,
      });
    } else if (window.scrollY < 200 && !storyHeaderInView && desktop) {
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: true,
      });
    }
  };

  const throttledHandleScroll = throttle(handleScroll, 200);
  useEffect(() => {
    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      throttledHandleScroll.cancel();
    };
  }, [throttledHandleScroll]);

  function stripHTML(html) {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  }

  useEffect(() => {
    if (!isLiveBlog || !liveBlogStoryId) return;

    let mounted = true;

    const fetchLiveBlogStory = async () => {
      try {
        const res = await fetch(`${qtConfig["sketches-host"]}/api/v1/stories/${liveBlogStoryId}`);
        const json = await res.json();

        const cardsFromApi = get(json, ["story", "cards"], []);
        const isClosed = get(json, ["story", "metadata", "is-closed"]) === true;

        const cards = isClosed ? [...cardsFromApi].reverse() : cardsFromApi;

        const titles = [];
        const times = [];

        cards.forEach((card) => {
          const elements = card["story-elements"] || [];
          elements.forEach((el) => {
            if ((el.type === "title" || el.type === "text") && el.text) {
              titles.push(stripHTML(el.text.trim()));
              times.push(card["card-event-at"] || null);
            }
          });
        });

        if (mounted) {
          setApiTitles(titles);
          setApiTimes(times);
        }
      } catch (err) {
        console.error("Live blog API error", err);
      }
    };

    fetchLiveBlogStory();

    return () => {
      mounted = false;
    };
  }, [isLiveBlog, liveBlogStoryId]);

  // const displayTitles = desktop ? apiTitles.slice(0, 1) : desktop && isAdfreePage ? apiTitles.slice(0, 2) : apiTitles.slice(0, 2);
  const displayTitles = isAdfreePage ? apiTitles.slice(0, 2) : desktop ? apiTitles.slice(0, 1) : apiTitles.slice(0, 2);

  // const displayTimes = desktop ? apiTimes.slice(0, 1) : apiTimes.slice(0, 2);
  const displayTimes = isAdfreePage ? apiTimes.slice(0, 2) : desktop ? apiTimes.slice(0, 1) : apiTimes.slice(0, 2);

  // Track Load More button clicks
  const trackLoadMoreClick = (sectionName) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, props.pageType);
      window.dataLayer.push({
        event: "dl_cta_click",
        link_category: sectionName?.toLowerCase() || parentSectionCollectionName?.toLowerCase(),
        link_text: "load more",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(props.pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  if (!parentSecStories.length) {
    return <h1>{noStoriesFound}</h1>;
  }

  const getMoreStories = async (
    offset,
    limit,
    slug,
    setStories,
    storiesToRender,
    setStoriesToRender,
    stories,
    setHaveMoreStories
  ) => {
    await getLoadMoreStories({
      offset: offset,
      limit: isAdfreePage && !isVisualStory ? limit : limit - 1,
      slug,
      shouldUseCollection: true,
      setStories,
      storiesToRender: storiesToRender,
      setStoriesToRender: setStoriesToRender,
      stories,
      isSectionPage: true,
      setHaveMoreStories,
    });
  };

  const ChildCollection = ({ slug, collection, pageType }) => {
    const [storiesToRender, setStoriesToRender] = useState(isAdfreePage ? 5 : 4);
    const sliceCount = isAdfreePage ? 5 : 4;
    const [childCollectionStories, setChildCollectionStories] = useState(collection?.items?.slice(0, sliceCount) || []);
    const [haveMoreStories, setHaveMoreStories] = useState(true);
    const storiesLoadLimit = 3;
    const childSectionSlug = props?.config?.sections?.filter((item) => item?.collection?.slug === slug)[0]?.slug;
    return (
      <div styleName="template-wrapper">
        <div styleName={`template-header ${isVideosPhotosSection ? "dark-headline" : ""}`}>
          <CollectionName collection={collection} />
          <Link href={`/${sectionSlug}/${childSectionSlug}`} styleName="link" disableAjaxLinks={true}>
            <span styleName="link-text">{STATIC_KEYWORDS.READ_MORE}</span>
            <SvgIconHandler type="arrow" width="24px" height="24px" viewBox="0 0 24 24" styleName="arrow-style" />
          </Link>
        </div>
        <FourColGrid
          collection={{ ...collection, items: childCollectionStories }}
          isLoadMoreVisible={false}
          showCollectionName={false}
          showReadTime={false}
          showMultipleAuthor={true}
          showAuthorContent={false}
          l2onl1page={true}
          pageType={pageType}
        />
        {!desktop && haveMoreStories && (
          <Button
            onClickFunction={() => {
              trackLoadMoreClick(collection?.name);
              getMoreStories(
                storiesToRender,
                storiesLoadLimit,
                slug,
                setChildCollectionStories,
                storiesToRender,
                setStoriesToRender,
                childCollectionStories,
                setHaveMoreStories
              );
            }}
          >
            {LOAD_MORE}
          </Button>
        )}
      </div>
    );
  };

  const MEDIA_BASE_URL = "https://media.fortuneindia.com/";

  const digitalCreatorOfTheMonthContainer = (sectionCollection) => {
    const items = get(sectionCollection, ["items"], []);
    if (!items.length) return null;

    // First item = featured creator
    const [featuredItem, ...remainingItems] = items;

    const getStory = (item) => get(item, ["story"], {});

    /**
     * Get hero image URL with fallback
     * Priority:
     * 1. alternative.home.default.hero-image.hero-image-url
     * 2. hero-image-s3-key
     */
    const getHeroImageUrl = (story, width) => {
      const directUrl = get(story, ["alternative", "home", "default", "hero-image", "hero-image-url"], "");

      if (directUrl) {
        return `${directUrl}?w=${width}&q=70`;
      }

      const s3Key = get(story, ["hero-image-s3-key"], "");
      if (s3Key) {
        return `${MEDIA_BASE_URL}${s3Key}?w=${width}&q=70`;
      }

      return "";
    };

    const getCreatorMonth = (story) => get(story, ["metadata", "story-attributes", "creatorofthemonth", 0], "");
    const getCreatorDesignation = (story) => get(story, ["metadata", "creator-designation"], "");

    const getCreatorName = (story) => get(story, ["headline"], "");
    const getCreatorDesc = (story) => get(story, ["subheadline"], "");
    const getStoryUrl = (story) => get(story, ["slug"], "#");

    return (
      <div className="digital-creator container" styleName="digitalcreator">
        {/* =========================
           SECTION HEADER
         ========================= */}
        <Headline type={1} customStyleName="section-main-headline">
          {sectionCollection.name}
        </Headline>

        {sectionCollection.summary && <div styleName="description">{sectionCollection.summary}</div>}

        {/* =========================
           FEATURED CREATOR
         ========================= */}
        {featuredItem &&
          (() => {
            const story = getStory(featuredItem);
            const imageUrl = getHeroImageUrl(story, 551);

            return (
              <div styleName="creator-main-container">
                <a href={`/${getStoryUrl(story)}`}>
                <div styleName="creator-image">
                  {imageUrl && (
                      <img
                        src={imageUrl}
                        alt={getCreatorName(story)}
                        title={getCreatorName(story)}
                        loading="lazy"
                        width="551"
                        height="551"
                      />
                  )}
                </div>

                <div styleName="creator-info">
                  {getCreatorMonth(story) && <small>{getCreatorMonth(story)}</small>}
                  <h2>
                    {getCreatorName(story)}
                  </h2>
                  <h3>{getCreatorDesignation(story)}</h3>
                  {getCreatorDesc(story) && <p>{getCreatorDesc(story)}</p>}
                </div>
                </a>
              </div>
            );
          })()}

        {/* =========================
           PREVIOUS CREATORS
         ========================= */}
        {remainingItems.length > 0 && (
          <div styleName="remain-creators">
            <div styleName="heading">Previous Month&apos;s Top Creators</div>

            <div styleName="creator-card-container">
              {remainingItems.map((item, index) => {
                const story = getStory(item);
                const imageUrl = getHeroImageUrl(story, 360);

                return (
                  <div styleName="creator-card-box" key={story.id || index}>
                    <div styleName="creator-card-image">
                      {imageUrl && (
                        <a href={`/${getStoryUrl(story)}`}>
                          <img
                            src={imageUrl}
                            alt={getCreatorName(story)}
                            title={getCreatorName(story)}
                            loading="lazy"
                            width="360"
                            height="360"
                          />
                        </a>
                      )}
                    </div>

                    <div styleName="creator-card-info">
                      {getCreatorMonth(story) && <small>{getCreatorMonth(story)}</small>}
                      <h2>
                        <a href={`/${getStoryUrl(story)}`}>{getCreatorName(story)}</a>
                      </h2>
                      <h3>{getCreatorDesignation(story)}</h3>
                      {/* {getCreatorDesc(story) && <p>{getCreatorDesc(story)}</p>} */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isDigitalCreator) {
    return digitalCreatorOfTheMonthContainer(sectionCollection);
  }

  return (
    <div styleName={`section-page-wrapper ${showDarkMode ? "dark-mode" : ""} ${isVisualStory ? "visual" : ""}`}>
      <div className="container" styleName={`${isBlackBg ? "budget-container" : ""} section-page`}>
        {budgetBannerDesktopImageUrl && budgetBannerMobileImageUrl && (
          <div styleName="budget-banner">
            {isClient && (
              <>
                {budgetBannerDesktopImageUrl && (
                  <BrowserView>
                    <img
                      src={budgetBannerDesktopImageUrl}
                      width={1140}
                      height={150}
                      alt="Budget 2026"
                      title="Budget 2026"
                      loading="lazy"
                    />
                  </BrowserView>
                )}

                {budgetBannerMobileImageUrl && (
                  <MobileView>
                    <img
                      src={budgetBannerMobileImageUrl}
                      width={360}
                      height={100}
                      alt="Budget 2026"
                      title="Budget 2026"
                      loading="lazy"
                    />
                  </MobileView>
                )}
              </>
            )}
          </div>
        )}
        {allSections?.length > 1 && (
          <div styleName="section-header-wrapper">
            <div styleName="section-header">
              {allSections?.map((section, index) => {
                const { "section-url": sectionurl, id, name, "display-name": displayName, slug } = section;
                const isActive = slug === props.data.section.slug ? "active" : "";
                return section?.data?.isDisabled ? null : (
                  <Link href={`${sectionurl}`} key={id} styleName={`section-name ${isActive}`} disableAjaxLinks={true}>
                    {index === 0 ? ` ${displayName || name}` : displayName || name}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        <h1 styleName="collection-name">{isMagazineLandingPage ? magazineTitle : `${parentSectionCollectionName}`}
          {sponsorList.length > 0 && (
            <div styleName="sponsor-logo">
              {Array.isArray(sponsorList) &&
                sponsorList.map((sponsorLogo, index) => (
                  <div styleName={`${index === activeSponsorContainer ? "is-active" : "is-hidden"} sponsor-logo-slider`} key={index}>
                    <p>{sponsorLogo.title}</p>
                    {sponsorLogo.anchor_url ? (<a
                      href={sponsorLogo.anchor_url}
                      target={sponsorLogo.target}
                      rel="noopener noreferrer"
                    >
                      <img
                        src={sponsorLogo.image_url}
                        alt={sponsorLogo.title}
                        width="111"
                        height="30"
                      />
                    </a>) : (
                      <img
                        src={sponsorLogo.image_url}
                        alt={sponsorLogo.title}
                        width="111"
                        height="30"
                      />
                    )}
                  </div>
                ))}
            </div>
          )}
        </h1>
        {!isVisualStory && (
          <>
            {isBudget ? (
              <div styleName={`${isAdfreePage ? "main-story-adfree" : ""} main-story-container`}>
                {sectionCollection?.summary && <YoutubeIframe collection={sectionCollection}></YoutubeIframe>}
                <div styleName="second-story">
                  <StoryCard story={parentSectionFirstStory} config={qtConfig}>
                    <div styleName="liveblog-card">
                      <LiveButton story={parentSectionFirstStory} />
                      <Link href={`/${parentSectionFirstStory.slug}`} disableAjaxLinks={true}>
                        <div styleName={`${isLiveBlog ? "" : "normal-card-content"} liveblog-card-content`}>
                          <Headline type={2} customStyleName="section-main-headline">
                            {parentSectionFirstStory.headline}
                          </Headline>
                        </div>
                        <div styleName={`liveblog-home-wrapper ${isLiveBlog ? "" : "normal-home-wrapper"}`}>
                          {displayTitles.length ? (
                            isLiveBlog &&
                            displayTitles.map((title, idx) => (
                              <Fragment key={idx}>
                                <div styleName="liveblog-timestamp">
                                  <svg
                                    class="live-icon"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="25"
                                    height="24"
                                    viewBox="0 0 25 24"
                                  >
                                    <path
                                      fill="#C6554F"
                                      d="M12 18a6 6 0 110-12 6 6 0 010 12zm0 2.5a8.5 8.5 0 110-17 8.5 8.5 0 010 17zm0-1a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
                                    />
                                  </svg>

                                  <AuthorWithTime
                                    config={qtConfig}
                                    story={
                                      displayTimes[idx]
                                        ? { ...parentSectionFirstStory, "last-published-at": displayTimes[idx] }
                                        : parentSectionFirstStory
                                    }
                                    hideAuthorImage={true}
                                    showAuthorContent={false}
                                  />
                                </div>

                                <Subheadline
                                  story={{ ...parentSectionFirstStory, subheadline: title }}
                                  truncateChars={140}
                                />
                              </Fragment>
                            ))
                          ) : (
                            <>
                              {/* <AuthorWithTime
                              config={qtConfig}
                              story={parentSectionFirstStory}
                              hideAuthorImage={true}
                              showAuthorContent={false}
                            /> */}
                              <Subheadline story={parentSectionFirstStory} truncateChars={140} />
                            </>
                          )}
                        </div>
                        <div styleName="live-blog-link-wrapper">
                          <span
                            styleName="live-blog-link"
                          >
                            {isLiveBlog ? "More Updates" : "Read More"}
                          </span>
                        </div>
                      </Link>
                    </div>
                  </StoryCard>
                </div>
                {!isAdfreePage && !premiumUser && (
                  <ListingAdSlot
                    adStyleName="ad-style-300x250"
                    adType="right_side"
                    pageType={props.pageType}
                    adSlotConfigName="mRec_ad"
                  />
                )}
              </div>
            ) : (
              <div styleName="top-section">
                <div styleName={`left-section ${isAdfreePage || premiumUser ? "premium-user" : ""}`}>
                  {isYoutubeTitle && sectionCollection?.summary ? (
                    <div
                      styleName={`${isVideosPhotosSection
                        ? "story-content-wrapper dark-theme-section"
                        : " story-content-wrapper light-theme-section"
                        }`}
                    >
                      <Headline type={2} customStyleName="section-main-headline">
                        {isYoutubeTitle}
                      </Headline>
                    </div>
                  ) : (
                    <div
                      styleName={`${isVideosPhotosSection
                        ? "story-content-wrapper dark-theme-section"
                        : " story-content-wrapper light-theme-section"
                        }`}
                    >
                      <Link href={`/${slug}`} disableAjaxLinks={true}>
                        <Headline type={2} customStyleName="section-main-headline">
                          {parentSecAltHeadline || headline}
                        </Headline>
                      </Link>

                      <div styleName="story-content">
                        <AuthorWithTime story={parentSectionFirstStory} showAuthorContent={false} />
                      </div>
                      {(isAdfreePage || premiumUser) && (
                        <div styleName={`${isAdfreePage ? subHeadlineClass : ""}`}>
                          <Subheadline story={parentSectionFirstStory} />
                        </div>
                      )}
                    </div>
                  )}
                  {sectionCollection?.summary && isYoutubeTitle ? (
                    <YoutubeIframe collection={sectionCollection}></YoutubeIframe>
                  ) : (
                    <HeroImage story={parentSectionFirstStory} aspectRatio={[[16, 9]]} />
                  )}
                </div>
                {!isAdfreePage && !premiumUser && (
                  <ListingAdSlot
                    adStyleName="ad-style-300x250"
                    adType="right_side"
                    pageType={props.pageType}
                    adSlotConfigName="mRec_ad"
                  />
                )}
              </div>
            )}
          </>
        )}
        <div styleName={`${isAdfreePage ? `top-template no-ad-top-template ${borderClass}` : "top-template"}`}>
          {isVisualStory ? (
            <FourColPortraitStories
              collection={{ ...sectionCollection, items: secToNineParentStories }}
              isLoadMoreVisible={false}
              showCollectionName={false}
              showReadTime={false}
              pageType={props?.pageType}
              fetchPriorityHigh={true}
            />
          ) : (
            <FourColGrid
              collection={{ ...sectionCollection, items: secToNineParentStories }}
              isLoadMoreVisible={false}
              showCollectionName={false}
              showReadTime={false}
              showAuthorContent={false}
              sectionSlug={props?.data?.section?.slug}
              pageType={props.pageType}
            />
          )}
        </div>
        {!isAdfreePage && !isVisualStory && (
          <ListingAdSlot
            adStyleName="ad-slot-size-728x90-300x250"
            adType="below_top_listing"
            pageType={isPhotoSection ? "photo-listing" : isVideoSection ? "video-listing" : props.pageType}
            index={5}
          />
        )}
        {childCollections?.map((collection, index) => {
          const { name, slug, ["associated-metadata"]: { is_short_videos } = {} } = collection;

          return (
            <Fragment key={collection?.id}>
              {is_short_videos === true ? (
                <ArrowFourColPortraitStories collection={collection} pageType={props.pageType} />
              ) : slug === "videos" ? (
                <ArrowThreeColGrid collection={collection} columnFix={true} />
              ) : (
                <ChildCollection name={name} slug={slug} collection={collection} pageType={props.pageType} />
              )}

              {/* Ads for ALL collections EXCEPT shorts */}
              {slug !== "shorts" && !isAdfreePage && !premiumUser && desktop && index < 3 && (
                <div styleName="ad-container middle-ad">
                  <ListingAdSlot
                    adStyleName="ad-slot-size-728x90-300x250"
                    adType="collection_ad"
                    index={index}
                    pageType={props.pageType}
                  />
                </div>
              )}
            </Fragment>
          );
        })}

        {!isAdfreePage && !desktop && childCollections?.length ? (
          <ListingAdSlot
            adStyleName="ad-slot-size-300x250"
            adType="last_listing"
            index={2}
            pageType={props.pageType}
            adSlotConfigName={"mRec_ad"}
          />
        ) : null}
        {otherParentSecStories.length > 0 && (
          <div styleName="template-wrapper">
            {!isVisualStory ? (
              <div styleName="template-header">
                <h3 styleName="collection-name">{`All ${newSectionTitle
                  .replace(/^all\b\s*/i, "")
                  .replace(/\bnews\b$/i, "")
                  .trim()} News`}</h3>
              </div>
            ) : null}
            <div styleName="last-template">
              {isVisualStory ? (
                <FourColPortraitStories
                  collection={{ ...sectionCollection, items: otherParentSecStories }}
                  showReadTime={false}
                  pageType={props?.pageType}
                />
              ) : (
                <ThreeColGrid
                  collection={{ ...sectionCollection, items: otherParentSecStories }}
                  showReadTime={false}
                  page={isVideoSection ? "video-listing" : isPhotoSection ? "photo-listing" : props?.pageType}
                  initialLoadCount={11}
                  adType="grid_ad"
                  showAuthorContent={false}
                />
              )}
              {haveMoreStories &&
                otherParentSecStories.length >= (isAdfreePage ? 5 : 4) &&
                parentSecStories.length > (isAdfreePage ? 14 : 13) && (
                  <Button
                    onClickFunction={() => {
                      trackLoadMoreClick();
                      getMoreStories(
                        storiesToRender,
                        storiesLoadLimit,
                        get(props, ["data", "collection", "slug"], ""),
                        setOtherParentSecStories,
                        storiesToRender,
                        setStoriesToRender,
                        otherParentSecStories,
                        setHaveMoreStories
                      );
                    }}
                  >
                    {LOAD_MORE}
                  </Button>
                )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SectionPage.propTypes = {
  pageType: string,
  data: shape({
    collection: object,
    section: string,
  }),
};

export { SectionPage };
